import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import ProfileHeader from "../../../common/components/profile-header/profile-header-and-breadcrumbs";
import { routes } from "../../../constants/routes";
import { useLocation, useNavigate } from "react-router-dom";
import "./booking-requests.scss";
import { Box, IconButton } from "@mui/material";
import ListingTable from "../../../common/components/listing-table/list-table";
import {
  getBookingRequests,
  resendProviderPaymentLink,
} from "../../../api/provider-section/provider-service-management";
import { headers } from "./booking-requests-helper";
import BookingStatus from "./booking-status";
import AdminPaginationComponent from "../../../common/components/admin-pagination-component/admin-pagination-component";
import { useSetLoading } from "../../../common/hooks/appearance";
import { AlertComponent } from "../../../common/components";
import ActionConfirmationPopup from "../../../common/components/action-confirmation-popup/action-confirmation-popup";
import resendMailIcon from "../../../assets/icons/email-resend-outline.svg";
import ServiceListSearchAndFilters from "./booking-requests-search-filters";
import _ from "lodash";
import dayjs from "dayjs";
import BookingAppointmentCalendar from "./booking-appointment-calendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import BookingDetails from "./booking-details";
import { getBookingAppointmentDetails } from "../../../api/booking.api";

const BookingRequests: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const breadcrumbLink = [
    {
      label: "Contract Orders",
      link: routes.BOOKING_AND_APPOINTMENT,
      handleClick: () => { },
      disablePointerEvents: true,
    },
  ];
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  //  store numeric booking id (item.id) for details API
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(
    null,
  );
  const [detailsData, setDetailsData] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [appointmentByOrderId, setAppointmentByOrderId] = useState<
    Record<string, { date: string; time: string }>
  >({});

  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [error, setError] = useState("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [resendLoadingByRow, setResendLoadingByRow] = useState<
    Record<string, boolean>
  >({});
  const [sendPaymentPopupOpen, setSendPaymentPopupOpen] = useState(false);
  const [selectedPendingOrder, setSelectedPendingOrder] = useState<any>(null);
  const [selectedPendingOrderEmail, setSelectedPendingOrderEmail] = useState("");

  const [searchText, setSearchText] = useState<any>(
    location?.state?.searchText || "",
  );
  const [activeFilters, setActiveFilters] = useState<any>(
    location?.state?.activeFilters || {},
  );

  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isResetSearch, setIsResetSearch] = useState(false);
  const searchRef = useRef<{ clear: () => void }>(null);
  const isSearchTriggered = useRef(false);
  const pageSize = 10;

  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1);
      setSearchText(text);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    [],
  );

  const fetchAppointmentForListing = async (
    id: string,
  ): Promise<{ id: string; date: string; time: string }> => {
    try {
      const res = await getBookingAppointmentDetails({
        timezone: userTimeZone,
        bookingId: id,
      });
      const apt = res?.data?.data?.appointment;
      if (!apt) return { id, date: "", time: "" };
      const rawDate = apt.appointmentDate ?? "";
      const rawTime = apt.appointmentTime ?? "";
      const date = rawDate.includes(" at ")
        ? rawDate.split(" at ")[0]
        : rawDate;
      const time = rawTime.includes(" at ")
        ? rawTime.split(" at ")[1]
        : rawTime;
      return { id, date, time };
    } catch {
      return { id, date: "", time: "" };
    }
  };

  const fetchProviderRequests = async (
    page: number,
    search: string,
    filters: any,
  ) => {
    setLoading(true);
    try {
      const params: any = {
        page,
        pageSize,
        timezone: userTimeZone,
      };

      if (search) params.searchText = search;
      if (filters?.status) params.status = filters.status;

      if (filters?.fromDate && filters?.toDate) {
        params.fromDate = dayjs(filters.fromDate).startOf("day").toISOString();
        params.toDate = dayjs(filters.toDate).endOf("day").toISOString();
      }

      if (filters?.appointmentDate) {
        params.appointmentDate = dayjs(filters.appointmentDate)
          .startOf("day")
          .toISOString();
      }

      const response = await getBookingRequests(params);

      if (response?.status === 200 || response?.data?.success) {
        const result = response?.data?.data?.result || [];
        setTotalRecords(response?.data?.data?.totalCount || 0);

        if (result.length > 0) {
          const appointmentResults = await Promise.all(
            result.map((item: any) =>
              item?.id != null
                ? fetchAppointmentForListing(String(item.id))
                : Promise.resolve({ id: "", date: "", time: "" }),
            ),
          );
          const appointmentMap: Record<string, { date: string; time: string }> =
            {};
          appointmentResults.forEach((r) => {
            if (r.id && (r.date || r.time))
              appointmentMap[r.id] = { date: r.date, time: r.time };
          });
          setAppointmentByOrderId(appointmentMap);
        } else {
          setAppointmentByOrderId({});
        }
        setData(result);
      } else {
        setAlertType("error");
        setAlertMessage("Failed to fetch Booking appointments.");
        setAlertOpen(true);
      }
    } catch (error: any) {
      setAlertType("error");
      setAlertMessage(
        error?.response?.data?.message ||
        "Failed to fetch Booking appointments.",
      );
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  //  details API expects numeric bookingId (item.id from getBookingRequests)
  const fetchDetails = async (bookingId: string) => {
    if (!bookingId || bookingId === "undefined") return;

    setDetailsLoading(true);
    setDetailsData(null);
    try {
      const res = await getBookingAppointmentDetails({
        timezone: userTimeZone,
        bookingId: bookingId, //  numeric id (string ok)
      });
      setDetailsData(res?.data?.data ?? null);
    } catch (e) {
      setDetailsData(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  //  row.id is numeric booking id (used for details)
  const handleRowClick = (row: any) => {
    const bookingNumericId = row?.id;
    if (!bookingNumericId) return;

    setSelectedBookingId(String(bookingNumericId));
    fetchDetails(String(bookingNumericId));
  };

  useEffect(() => {
    setSelectedBookingId(null);
    setDetailsData(null);
    setAppointmentByOrderId({});

    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchProviderRequests(pageToUse, searchText || "", activeFilters);
    isSearchTriggered.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchText, activeFilters]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
  };

  const EMPTY_FILTERS = {
    fromDate: "",
    toDate: "",
    appointmentDate: "",
    status: [],
  };

  const handleResendPaymentLink = async (item: any) => {
    const leadId = item?.leadId;
    const rowKey = String(item?.orderId ?? item?.id ?? leadId ?? "");

    if (!leadId) {
      setAlertType("error");
      setAlertMessage("Lead ID not found for this order.");
      setAlertOpen(true);
      return;
    }

    setResendLoadingByRow((prev) => ({ ...prev, [rowKey]: true }));
    setLoading(true);
    try {
      const response = await resendProviderPaymentLink(leadId);

      setAlertType("success");
      setAlertMessage(
        response?.data?.message ||
        response?.data?.data?.message ||
        "Payment link sent successfully.",
      );
      setAlertOpen(true);
    } catch (error: any) {
      setAlertType("error");
      setAlertMessage(
        error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        "Failed to send payment link.",
      );
      setAlertOpen(true);
    } finally {
      setLoading(false);
      setResendLoadingByRow((prev) => ({ ...prev, [rowKey]: false }));
    }
  };

  const getEmailFromRow = (item: any): string => {
    return (
      item?.customer?.email ??
      item?.user?.email ??
      item?.customerEmail ??
      item?.email ??
      item?.lead?.email ??
      ""
    );
  };

  const getBookingIdCandidatesFromRow = (item: any): Array<number | string> => {
    const rawCandidates = [item?.id, item?.orderId, item?.bookingId];

    const normalized = rawCandidates
      .filter((v) => v !== undefined && v !== null && String(v).trim() !== "")
      .map((v) => {
        if (typeof v === "string") {
          const digits = v.replace(/[^\d]/g, "");
          return digits || v;
        }
        return v;
      })
      .filter((v) => String(v) !== "0");

    return Array.from(new Set(normalized));
  };

  const resolveCustomerEmail = async (item: any): Promise<string> => {
    const directEmail = getEmailFromRow(item);
    if (directEmail) return directEmail;

    const bookingIds = getBookingIdCandidatesFromRow(item);
    for (const bookingId of bookingIds) {
      try {
        const response = await getBookingAppointmentDetails({
          timezone: userTimeZone,
          bookingId,
        });
        const email = response?.data?.data?.customer?.email;
        if (email) return email;
      } catch {
        // try next candidate id
      }
    }

    return "";
  };

  const openSendPaymentPopup = async (item: any) => {
    setSelectedPendingOrder(item);
    const email = await resolveCustomerEmail(item);
    setSelectedPendingOrderEmail(email || "-");
    setSendPaymentPopupOpen(true);
  };

  const formattedData = useMemo(() => {
    if (Array.isArray(data)) {
      return data.map((item: any) => {
        const type = item?.orderProductType;
        const formattedBookingType =
          type === "at_need" || type === "at-need"
            ? "Immediate Need"
            : type === "pre_need" || type === "pre-need"
              ? "Preneed"
              : type === "both"
                ? "Preneed, Immediate Need"
                : type;

        return {
          //  IMPORTANT: numeric id needed for details
          id: item.id,

          //  for display only (order code)
          bookingId: item.orderId,

          customerName: `${item?.user?.first_name || ""} ${item?.user?.last_name || ""}`,
          bookingType: formattedBookingType || "",
          requestedDateAndTime: (() => {
            const fromDetails = appointmentByOrderId[String(item?.id)];
            if (fromDetails && (fromDetails.date || fromDetails.time)) {
              const dateStr = fromDetails.date || "--";
              const timeStr = fromDetails.time || "--";
              return `${dateStr}  |  ${timeStr}`;
            }
            return "";
          })(),
          orderedDate: item?.submissionDate
            ? dayjs(item.submissionDate).format("DD/MM/YYYY")
            : "-",
          status: item.status === "paid" ? "confirmed" : item.status,
          _original: item,
        };
      });
    }
    return [];
  }, [data, appointmentByOrderId]);

  useEffect(() => {
    if (
      location.state &&
      location.state.alertMessage &&
      location?.state?.fromView
    ) {
      setAlertMessage(location.state.alertMessage);
      setAlertType(location.state.alertType || "info");
      setAlertOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }

    if (location.state && location.state.searchText) {
      const searchValue = location.state.searchText;
      setSearchText(searchValue);
      navigate(location.pathname, { replace: true, state: {} });
    }

    if (
      location.state &&
      Object.keys(location?.state?.activeFilters || {}).length > 0
    ) {
      const filters = location.state.activeFilters;
      setActiveFilters(filters);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;

    if (activeFilters.fromDate || activeFilters.toDate) {
      count += 1;
    }
    if (activeFilters.appointmentDate) {
      count += 1;
    }
    if (
      Array.isArray(activeFilters.status) &&
      activeFilters.status.length > 0
    ) {
      count += 1;
    }

    return count;
  }, [activeFilters]);

  const noResultsTxt = useMemo(() => {
    if (loading) return "Loading Orders...";
    if (searchText && appliedFilterCount === 0)
      return "No Orders found matching your criteria.";
    if (!searchText && appliedFilterCount > 0)
      return "No Orders found matching filter criteria.";
    if (searchText && appliedFilterCount > 0)
      return "No Orders found for search and filter criteria.";
    return "No order requests found.";
  }, [loading, searchText, appliedFilterCount]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);
  const refetchProviderRequests = () => {
    fetchProviderRequests(currentPage, searchText, activeFilters);
  };
  return (
    <>
      <div className="provider-requests-main-wrapper">
        <div
          style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          <ProfileHeader breadcrumbLink={breadcrumbLink} mode="provider" />

          {/* <div className="booking-sub-tabs">
            <button
              className={activeTab === "booking" ? "active" : ""}
              onClick={() => setActiveTab("booking")}
            >
              Contract Orders
            </button>

            <button
              className={activeTab === "leads" ? "active" : ""}
              onClick={() => setActiveTab("leads")}
            >
              Leads
            </button>
          </div> */}

          <div
            className="provider-requests-filter-and-list-wrapper"
            style={{
              display: !isViewOpen ? "flex" : "none",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              paddingBottom: totalRecords === 0 ? "10px" : "0px",
            }}
          >
            {/*  ALWAYS render the component so icons are always visible */}
            {/*  BUT: hide search/filter/helper UI unless viewMode === "list" */}
            <div className="list-search-and-filters service-list-search-and-filters">
              <div style={{ width: "100%" }}>
                <ServiceListSearchAndFilters
                  showListControls={viewMode === "list"}
                  onSearch={handleSearch}
                  onFilterChange={(filters) => {
                    setActiveFilters(filters);
                    setCurrentPage(1);
                  }}
                  onResetFilters={() => {
                    setActiveFilters(EMPTY_FILTERS);
                    setCurrentPage(1);
                  }}
                  filterValues={activeFilters}
                  appliedFiltersCount={appliedFilterCount}
                  isFilterOpen={isOpenFilter}
                  setIsFilterOpen={setIsOpenFilter}
                  searchRef={searchRef}
                  isReset={isResetSearch}
                  localSearchText={searchText}
                  placeholder="Search by Customer Name, Order ID"
                  viewMode={viewMode}
                  setViewMode={(mode) => {
                    setViewMode(mode);
                    // switching view closes details
                    setSelectedBookingId(null);
                    setDetailsData(null);
                  }}
                />
              </div>
            </div>

            {/*  Hide only the "search/filter/helper" area via CSS when not list view */}
            {/* Add this CSS block in booking-requests.scss:
                  .hide-search-filter-only .search-and-filter-container,
                  .hide-search-filter-only .filters-container,
                  .hide-search-filter-only .helper-text {
                    display: none !important;
                  }
                 Since we don't know your internal classnames, easiest is to wrap and hide the whole left block.
              */}

            {/*  LIST VIEW */}
            {viewMode === "list" && (
              <>
                {selectedBookingId ? (
                  <BookingDetails
                    booking={null}
                    apiDetails={detailsData}
                    loading={detailsLoading}
                    onBack={() => {
                      setSelectedBookingId(null);
                      setDetailsData(null);
                      refetchProviderRequests();
                    }}
                    orderId={selectedBookingId}
                    fetchDetails={fetchDetails}
                  />
                ) : (
                  <>
                    <div className="resend-credentials-container">
                      <div className="resend-credentials-option">
                        <div className="icon-circle">
                          <img src={resendMailIcon} alt="resend" />
                        </div>
                        <span>Resend Payment Link Option</span>
                      </div>
                    </div>
                    <div className="provider-requests-listing-table-wrapper">
                      <ListingTable
                        headers={headers}
                        data={formattedData?.map((item) => ({
                          ...item,
                          status: (
                            <Box display="flex" alignItems="center" gap={1}>
                              <BookingStatus
                                status={
                                  item.status
                                    ? item.status.charAt(0).toUpperCase() +
                                    item.status.slice(1)
                                    : ""
                                }
                              />
                              {String(item.status || "").toLowerCase() ===
                                "pending" && item?._original?.leadId != null && item?._original?.orderProductType !== "pre-need" && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openSendPaymentPopup(item?._original);
                                    }}
                                    disabled={
                                      resendLoadingByRow[String(item?.id ?? "")]
                                    }
                                    title="Send payment link"
                                  >
                                    <img
                                      src={resendMailIcon}
                                      alt="Send payment link"
                                      className="resend-password-icon"
                                    />
                                  </IconButton>
                                )}
                            </Box>
                          ),
                        }))}
                        noResultsText={noResultsTxt}
                        onRowClick={handleRowClick}
                      />

                      <div
                        style={{ display: isOpenFilter ? "flex" : "none" }}
                        className="provider-requests-filter-options filters-options-main"
                      ></div>
                    </div>

                    {totalRecords > 0 && (
                      <AdminPaginationComponent
                        totalRecords={totalRecords}
                        pageSize={pageSize}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                      />
                    )}
                  </>
                )}
              </>
            )}

            {/*  CALENDAR VIEW */}
            {viewMode === "calendar" && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <BookingAppointmentCalendar />
              </LocalizationProvider>
            )}
          </div>

          {/* {activeTab === "leads" && (
            <div className="provider-requests-filter-and-list-wrapper">
              <Leads />
            </div>
          )} */}
        </div>

        <AlertComponent
          open={alertOpen}
          type={alertType}
          message={alertMessage || ""}
          onClose={() => setAlertOpen(false)}
        />
        <ActionConfirmationPopup
          open={sendPaymentPopupOpen}
          title="Send Payment Link"
          note={`Are you sure you want to send a payment link to ${selectedPendingOrderEmail || "-"}?`}
          confirmButtonText="Confirm"
          onClose={() => {
            setSendPaymentPopupOpen(false);
            setSelectedPendingOrder(null);
            setSelectedPendingOrderEmail("");
          }}
          onConfirm={async () => {
            await handleResendPaymentLink(selectedPendingOrder);
            setSendPaymentPopupOpen(false);
            setSelectedPendingOrder(null);
            setSelectedPendingOrderEmail("");
          }}
          variant="success"
          showIcon={false}
        />
      </div>
    </>
  );
};

export default BookingRequests;
